import React, { useEffect, useState } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import {
  Button,
  Flex,
  IconButton,
  Select,
  Text,
} from "@chakra-ui/react";
import colors from "common/theme/foundations/colors";

const Pagination = (props) => {
  const {
    gotoPage,
    gopageValue,
    setGopageValue,
    pageCount,
    canPreviousPage,
    previousPage,
    canNextPage,
    pageOptions,
    setPageSize,
    nextPage,
    pageSize,
    pageIndex,
    dataLength,
    filters,
    setFilters,
    smallView = false,
    isPagination = true,
    allData = [],
  } = props;

  const currentPageName =
    window.location.hash.split("/crm/")[1]?.split("/")[0] || "defaultPage";

  useEffect(() => {
    const savedState = JSON.parse(localStorage.getItem("paginationState"));
    if (savedState && savedState.pageName === currentPageName) {
      const {
        pageIndex: savedPageIndex,
        pageSize: savedPageSize,
        filters: savedFilters,
      } = savedState;
      if (savedPageIndex !== undefined && savedPageSize !== undefined) {
        gotoPage(savedPageIndex);
        setPageSize(savedPageSize);
      }
      if (savedFilters !== undefined) {
        setFilters(savedFilters);
      }
    }
  }, [currentPageName, setFilters, gotoPage, setPageSize]);

  const handlePageChange = (pageIndex) => {
    const newPage = pageIndex + 1;
    setGopageValue(newPage);
    gotoPage(pageIndex);

    const savedState = JSON.parse(localStorage.getItem("paginationState")) || {};
    const updatedState = {
      ...savedState,
      currentPage: newPage,
    };
    localStorage.setItem("paginationState", JSON.stringify(updatedState)); // Store the updated page in localStorage
  };

  const handlePageSizeChange = (newSize) => {
    if (newSize !== pageSize) {
      setPageSize(newSize);
      gotoPage(0); // Reset to the first page when page size changes

      const savedState = JSON.parse(localStorage.getItem("paginationState")) || {};
      const updatedState = {
        ...savedState,
        pageSize: newSize,
        currentPage: 1, // Reset currentPage to 1
      };
      localStorage.setItem("paginationState", JSON.stringify(updatedState)); // Save updated pageSize in localStorage
    }
  };

  const totalPages = isPagination
    ? pageCount
    : Math.ceil(allData.length / pageSize);

  const totalDataLength = isPagination
    ? dataLength
    : allData.length;

  const startResult = pageIndex * pageSize + 1;
  const endResult = Math.min((pageIndex + 1) * pageSize, totalDataLength);

  const getPageNumbers = () => {
    const currentPage = pageIndex + 1;
    const pageNumbers = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);

      if (currentPage > 3) {
        pageNumbers.push("...");
      }

      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (currentPage < totalPages - 2) {
        pageNumbers.push("...");
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  return (
    <>
      <Flex
        className="paginationContainer"
        justifyContent={pageOptions?.length !== 1 ? "space-between" : "end"}
        mt={2}
        alignItems="center"
      >
        {pageOptions?.length !== 1 && (
          <div>
            <Text
              className={smallView ? "smallPaginationMsg" : "paginationMsg"}
              flexShrink="0"
              mr={8}
            >
              Showing <span>{startResult}</span>-<span>{endResult}</span> out of{" "}
              <span>{totalDataLength}</span> results
            </Text>
          </div>
        )}

        <div className="pageContainer">
          {pageOptions?.length !== 1 && (
            <IconButton
              className={
                smallView
                  ? "smallPaginationBtn paginationControllerBtn"
                  : "paginationBtn paginationControllerBtn"
              }
              onClick={() => {
                if (isPagination) {
                  previousPage();
                } else if (pageIndex > 0) {
                  handlePageChange(pageIndex - 1);
                }
              }}
              isDisabled={isPagination ? !canPreviousPage : pageIndex <= 0}
              icon={<ChevronLeftIcon h={6} w={6} />}
              mr={2}
            />

          )}

          {getPageNumbers().map((num, index) => (
            <Button
              className={smallView ? "smallPaginationBtn" : "paginationBtn"}
              key={index}
              onClick={() => num !== "..." && handlePageChange(num - 1)}
              isDisabled={num === "..."}
              border={
                num === pageIndex + 1
                  ? "none"
                  : `1px solid ${colors.borderColor}`
              }
              bg={num === pageIndex + 1 ? colors.themeColor : colors.white}
              color={num === pageIndex + 1 ? colors.white : colors.text.primary}
              fontWeight={num === pageIndex + 1 ? "700" : "400"}
              _hover={{
                bg:
                  num !== pageIndex + 1
                    ? colors.btnHoverBg
                    : colors.themeColor,
              }}
              mx={1}
            >
              {num}
            </Button>
          ))}

          {pageOptions?.length !== 1 && (
            <IconButton
              className={
                smallView
                  ? "smallPaginationBtn paginationControllerBtn"
                  : "paginationBtn paginationControllerBtn"
              }
              onClick={() => {
                if (isPagination) {
                  nextPage();
                } else if (pageIndex < totalPages - 1) {
                  handlePageChange(pageIndex + 1);
                }
              }}
              isDisabled={isPagination ? !canNextPage : pageIndex >= totalPages - 1}
              icon={<ChevronRightIcon h={6} w={6} />}
              ml={2}
            />

          )}

          <Select
            ml={4}
            w={32}
            value={pageSize}
            className={
              smallView
                ? "pagination-select smallPagination-select"
                : "pagination-select"
            }
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
          >
            {[5, 10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Select>
        </div>
      </Flex>
    </>
  );
};

export default Pagination;
